import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiMagnifyingGlassMinusLight, PiMagnifyingGlassPlusLight } from 'react-icons/pi';

/**
 * THE zoom control for the document viewer.
 *
 * One control for every readable type — Word, text, PDF and the sheet grid all
 * take the same factor, so "zoom" means the same thing whatever is open and the
 * level survives switching from one document to the next.
 */

export const ZOOM_LEVELS = [0.5, 0.75, 0.9, 1, 1.15, 1.3, 1.5, 1.75, 2, 2.5, 3];
export const DEFAULT_VIEWER_ZOOM = 1;

const ZOOM_STORAGE_KEY = 'pons:viewer:zoom';

/** Nearest declared level in `direction`; clamps at the ends. */
export const stepZoom = (current: number, direction: 1 | -1): number => {
  if (direction === 1) {
    return ZOOM_LEVELS.find((level) => level > current + 0.001) ?? ZOOM_LEVELS[ZOOM_LEVELS.length - 1];
  }
  const below = ZOOM_LEVELS.filter((level) => level < current - 0.001);
  return below.length > 0 ? below[below.length - 1] : ZOOM_LEVELS[0];
};

/** The remembered level, or 100% when nothing is stored or it is out of range. */
export const readStoredZoom = (): number => {
  try {
    const raw = window.localStorage.getItem(ZOOM_STORAGE_KEY);
    if (!raw) return DEFAULT_VIEWER_ZOOM;
    const value = Number(raw);
    if (!Number.isFinite(value)) return DEFAULT_VIEWER_ZOOM;
    return Math.min(Math.max(value, ZOOM_LEVELS[0]), ZOOM_LEVELS[ZOOM_LEVELS.length - 1]);
  } catch {
    return DEFAULT_VIEWER_ZOOM;
  }
};

export const writeStoredZoom = (zoom: number): void => {
  try {
    window.localStorage.setItem(ZOOM_STORAGE_KEY, String(zoom));
  } catch {
    /* a private-mode browser is not a reason to fail a zoom */
  }
};

interface ViewerZoomControlProps {
  zoom: number;
  onZoomChange: (zoom: number) => void;
  /** Slimmer padding for the fullscreen reading bar. */
  compact?: boolean;
  className?: string;
}

const ViewerZoomControl: React.FC<ViewerZoomControlProps> = ({
  zoom,
  onZoomChange,
  compact = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const atMin = zoom <= ZOOM_LEVELS[0] + 0.001;
  const atMax = zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1] - 0.001;

  const buttonClass = (disabled: boolean) =>
    `flex items-center justify-center ${compact ? 'p-1' : 'p-1.5'} rounded transition-colors ${
      disabled
        ? 'text-fg-muted/50 cursor-not-allowed'
        : 'text-fg-body hover:bg-surface-sunken/70 hover:text-champagne'
    }`;

  return (
    <div
      className={`glass-bar inline-flex items-center gap-0.5 rounded-lg border border-hairline-soft ${
        compact ? 'px-0.5 py-0.5' : 'px-1 py-0.5'
      } ${className}`}
      data-testid="viewer-zoom-control"
    >
      <button
        type="button"
        onClick={() => onZoomChange(stepZoom(zoom, -1))}
        disabled={atMin}
        className={buttonClass(atMin)}
        title={t('Zoom out')}
        aria-label={t('Zoom out')}
      >
        <PiMagnifyingGlassMinusLight className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => onZoomChange(DEFAULT_VIEWER_ZOOM)}
        className="px-1.5 text-[11px] tabular-nums text-fg-body hover:text-champagne transition-colors"
        title={t('Reset zoom')}
      >
        {Math.round(zoom * 100)}%
      </button>
      <button
        type="button"
        onClick={() => onZoomChange(stepZoom(zoom, 1))}
        disabled={atMax}
        className={buttonClass(atMax)}
        title={t('Zoom in')}
        aria-label={t('Zoom in')}
      >
        <PiMagnifyingGlassPlusLight className="w-4 h-4" />
      </button>
    </div>
  );
};

export default ViewerZoomControl;
